import type { ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asCheck, asDateTime, asLabel, column, createdByField, field, lastModifiedByField, type Row, related, text } from './kit';

const def = OBJECT_DEFS['deliverable-types'];

/** Long text with its line breaks kept: the spec is markdown, the template HTML. */
const asLines = (value: unknown) =>
  typeof value === 'string' && value.trim() !== '' ? <div className="whitespace-pre-wrap">{value}</div> : text(value);

/** The blueprint of one kind of deal document (0105). Rationale: deliverable-types.md. */
export const deliverableTypes: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Deliverable Type',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('name', 'Name'),
    field('code', 'Code', { help: 'What the skills ask for, e.g. proposal or remaining-plan.' }),
    field('renders_from', 'Renders From', { format: asLabel, help: 'The record it is rendered from: a quote (the quote builder) or a project (the project planner).' }),
    field('audience', 'Audience'),
    field('signed', 'Signed', { format: asCheck }),
    field('active', 'Active', { format: asCheck }),
    field('sort_order', 'Sort Order'),
    field('job', 'Job', { wide: true }),
    field('when_used', 'When Used', { wide: true }),
    field('research_tags', 'Research Tags', { help: 'Research finding tags pulled for the document itself.' }),
    field('never_include', 'Never Include', { wide: true, format: asLines }),
    field('spec', 'Spec', { wide: true, format: asLines, help: 'Markdown: sections in order, the data each reads, the research to pull.' }),
    field('template_html', 'Template HTML', { wide: true, format: asLines }),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['name', 'code', 'renders_from', 'audience', 'signed', 'active', 'sort_order', 'job', 'when_used', 'research_tags'] },
    { key: 'never', label: 'Never Include', fields: ['never_include'] },
    { key: 'spec', label: 'Spec', fields: ['spec'] },
    { key: 'template', label: 'Template', collapsed: true, fields: ['template_html'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('guidelines', 'Guidelines', 'guidelines', [
      column('name', 'Guideline', { primary: true }),
      column('enforcement', 'Enforcement', { format: asLabel }),
      column('weight', 'Weight', { align: 'right' }),
    ]),
    related('deliverables', 'Deliverables', 'deliverables', [
      column('name', 'Deliverable', { primary: true }),
      column('status', 'Status', { format: asLabel }),
      { ...column('is_exemplar', 'Exemplar'), render: (row) => asCheck(row.is_exemplar) },
    ]),
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'code', 'audience'],
};
